import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createVNode as _createVNode, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { class: "text-lg-bold text-center text-racq-dark-grey mb-6 mt-4" }
const _hoisted_3 = { class: "flex flex-col gap-5" }
const _hoisted_4 = { class: "text-racq-med-grey text-base" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = {
  key: 0,
  class: "loader spinner-border spinner-border-sm",
  role: "status",
  "aria-hidden": "true"
}
const _hoisted_7 = {
  key: 1,
  class: "flex flex-col items-center justify-center gap-2 py-10"
}
const _hoisted_8 = {
  key: 2,
  class: "flex flex-col items-center justify-center gap-4 py-10"
}

import { ref, computed, onMounted, nextTick } from 'vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import { Field } from '@sitecore-jss/sitecore-jss-vue';
import { InsuranceService as _insuranceService } from '@/services/store/insurance.service';
import ToggleSwitchCard from '@/components/Toggle/ToggleSwitchCard.vue';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { InformationBoxTypes } from '@/interfaces/forms/information-box.interface';
import { IToggleSwitchCardItem } from '@/interfaces/components/toggle-switch-card.interface';
import { IUpdateRenewalOfferRequest } from '@/interfaces/insurance/renewal.interface';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { FormEvents } from '@/enums/form-events.enum';
import { getInsuranceTypeDisplayName, InsurancePolicy } from '@/interfaces';
import { IAnalyticsPaymentDetail, IFormAnalytics } from '@/interfaces/forms/analytics-interface';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useAccountStore, useAnalyticsStore } from '@/store';
import { OFF, ON } from '@/constants/general-words.const';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { getCurrentPolicy } from './ProductDetailsHelper';

interface AutoRenewSettingsModalFields {
  // content / copy
  modalTitle: Field<string>;
  modalSummary: Field<string>;
  currentPolicy: InsurancePolicy;
  currentRenewalPreference: boolean;
  autoRenewToggleCardTitle: Field<string>;
  autoRenewToggleCardSummary: Field<string>;
  autoRenewOffDisclaimerText: Field<string>;
  autoRenewOnDisclaimerText: Field<string>;
  updateCtaLabel: Field<string>;
  successMessage: Field<string>;
  failureMessage: Field<string>;
}

interface Props {
  /** v-model visibility */
  modelValue: boolean;
  closeOnEsc?: boolean;
  autoRenewSettingsModal: AutoRenewSettingsModalFields;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AutoRenewSettingsModal',
  props: {
    modelValue: { type: Boolean, default: false },
    closeOnEsc: { type: Boolean, default: true },
    autoRenewSettingsModal: { default: () => ({
    modalTitle: {
      value: 'Auto renew settings',
    },
    modalSummary: {
      value: 'Please specify your auto renew preferences.',
    },
    currentPolicy: {
      policyNumber: '405467',
    },
    currentRenewalPreference: false,
    autoRenewToggleCardTitle: { value: 'Auto renew' },
    autoRenewToggleCardSummary: { value: 'Automatically renew your policy' },
    autoRenewOffDisclaimerText: {
      value:
        'Your policy will not automatically renew when it reaches the end of the of its term. You will need to contact us in the renewal period to indicate that you wish to renew.',
    },
    autoRenewOnDisclaimerText: {
      value:
        'Your policy will automatically renew when it reaches the end of the of its term and we will automatically deduct payments from your nominated account ',
    },
    updateCtaLabel: { value: 'Update' },
    successMessage: {
      value: '<p class="text-lg-bold mt-2 text-center font-family-sans">Auto renew is now {{autoRenewStatus}}.</p>',
    },
    failureMessage: {
      value:
        '<p class="text-lg-bold text-center font-family-sans">Unable to complete your request</p><p class="text-base racq-med-grey text-center mt-6 font-family-sans">We could not update your auto renew settings. Please call us on <a href="tel:131905">13 1905</a> to discuss this further.</p>',
    },
  }) }
  },
  emits: ["updateAutoRenewState"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const accountStore = useAccountStore();
const analyticsStore = useAnalyticsStore();

// ---- Types ----

const props = __props;

const showModal = ref(false);
const toggleItems = ref<IToggleSwitchCardItem[]>([]);
const isSubmitted = ref(false);
const isLoading = ref(false);
const isError = ref(false);

const emit = __emit;

__expose({
  showModal: async () => {
    // Reset modal state
    isSubmitted.value = false;
    isError.value = false;
    isLoading.value = false;

    // Reset toggle to original preference
    const autoRenewSettingsToggle = [
      {
        id: '1',
        title: props.autoRenewSettingsModal.autoRenewToggleCardTitle?.value,
        description: props.autoRenewSettingsModal.autoRenewToggleCardSummary?.value,
        modelValue: props.autoRenewSettingsModal.currentRenewalPreference,
      },
    ] as IToggleSwitchCardItem[];
    toggleItems.value = autoRenewSettingsToggle;

    showModal.value = true;
    await nextTick();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    pushAnalytics(FormEvents.START);
  },
});

const informationBoxDescription = computed(() => {
  const isAutoRenewOff = !toggleItems.value[0]?.modelValue;
  return isAutoRenewOff
    ? props.autoRenewSettingsModal.autoRenewOffDisclaimerText?.value
    : props.autoRenewSettingsModal.autoRenewOnDisclaimerText?.value;
});

const informationBoxType = computed(() => {
  const isAutoRenewOff = !toggleItems.value[0]?.modelValue;
  return isAutoRenewOff ? InformationBoxTypes.WARNING : InformationBoxTypes.SUCCESS;
});

const isUpdateEnabled = computed(() => {
  const currentToggleValue = toggleItems.value[0]?.modelValue;

  const originalPreference = props.autoRenewSettingsModal.currentRenewalPreference;
  return currentToggleValue !== originalPreference;
});

const autoRenewSuccessMessage = computed(() => {
  const currentPolicy = getCurrentPolicy(
    accountStore.identityDetails?.portfolio?.insurancePolicies as InsurancePolicy[]
  );

  const isAutoRenewOff = currentPolicy?.currentRenewalPreference;

  const successMessage = replaceBookmark(
    'autoRenewStatus',
    props.autoRenewSettingsModal.successMessage?.value,
    isAutoRenewOff ? ON : OFF
  );

  return replaceBookmark('autoRenewStatus', successMessage, isAutoRenewOff ? ON : OFF);
});

// Define Functions
// ------------------------------------------------------------- //

function handleToggleChange({ id, value }: { id: string; value: boolean }) {
  const idx = toggleItems.value.findIndex((t) => t.id === id);
  if (idx !== -1) {
    toggleItems.value[idx].modelValue = value;
  }
}

async function onUpdate() {
  try {
    isLoading.value = true;

    const response = await setRenewalPreference();

    //Force refresh of identity portfolios.
    await accountStore.fetchIdentityDetails(true);

    if (response.value) {
      emit('updateAutoRenewState', toggleItems.value[0]?.modelValue);
      isSubmitted.value = true;
      isError.value = false;
      pushAnalytics(FormEvents.COMPLETE, true);
    } else {
      isError.value = true;
    }
  } catch (error) {
    pushAnalytics(FormEvents.JEOPARDY, true);
    isError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function setRenewalPreference() {
  const response = await _insuranceService.setRenewalOfferPreference({
    policyNumber: props.autoRenewSettingsModal.currentPolicy.policyNumber,
    renewalPreference: toggleItems.value[0]?.modelValue,
  } as IUpdateRenewalOfferRequest);
  return response;
}

function closeModal() {
  showModal.value = false;
}

const pushAnalytics = (event: FormEvents, onUpdate: boolean = false) => {
  const currentPolicy = props.autoRenewSettingsModal.currentPolicy;
  const firstVehicle = currentPolicy?.coverages?.at(0);
  const model: IFormAnalytics = {
    event: event,
    form: {
      name: props.autoRenewSettingsModal.modalTitle?.value,
      detail: {
        makeName: firstVehicle?.vehicle?.make ?? '',
        modelName: firstVehicle?.vehicle?.model ?? '',
        vehicleYear: firstVehicle?.vehicle?.year ?? '',
      },
    },
  };
  const termEndDate = currentPolicy?.termEnd ? new Date(currentPolicy.termEnd) : null;
  model.form.termEndDate =
    termEndDate instanceof Date && !isNaN(termEndDate.getTime()) ? format(termEndDate, STANDARD_DATE_FORMAT) : '';

  if (currentPolicy?.coverages) {
    model.form.productName = getInsuranceTypeDisplayName(dictionary, currentPolicy.coverages[0]?.coverageCode);
  }

  if (!model.payment) {
    model.payment = {} as Partial<IAnalyticsPaymentDetail>;
  }

  if (event == FormEvents.JEOPARDY) {
    model.form.formErrorType = 'Auto renew error';
    model.form.formErrorDetail = 'Unable to complete your request';
  }

  event == FormEvents.START
    ? (model.payment.autoRenew = props.autoRenewSettingsModal.currentRenewalPreference ? ON : OFF)
    : (model.payment.autoRenew = toggleItems.value.at(0)?.modelValue ? ON : OFF);

  analyticsStore.pushAnalytics(model);
};

function onModalClose(e: any) {
  showModal.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalDialog, {
    modelValue: showModal.value,
    "onUpdate:modelValue": [
      _cache[0] || (_cache[0] = ($event: any) => ((showModal).value = $event)),
      _cache[1] || (_cache[1] = (value) => onModalClose(value))
    ],
    closeOnEsc: false,
    showXCloseButton: true,
    size: "md"
  }, {
    default: _withCtx(() => [
      (!isSubmitted.value && !isError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, _toDisplayString(props.autoRenewSettingsModal.modalTitle?.value), 1),
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("p", _hoisted_4, _toDisplayString(props.autoRenewSettingsModal.modalSummary?.value), 1),
              (_openBlock(), _createBlock(ToggleSwitchCard, {
                key: toggleItems.value[0]?.modelValue + '-' + showModal.value,
                items: toggleItems.value,
                onUpdate: handleToggleChange
              }, null, 8, ["items"])),
              _createVNode(InformationBox, {
                class: "mt-6",
                description: informationBoxDescription.value,
                type: informationBoxType.value
              }, null, 8, ["description", "type"]),
              _createElementVNode("button", {
                class: "btn btn-primary btn-md flex items-center justify-center gap-2 mt-4",
                disabled: !isUpdateEnabled.value || isLoading.value,
                onClick: onUpdate
              }, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                  : _createCommentVNode("", true),
                _createElementVNode("span", null, _toDisplayString(props.autoRenewSettingsModal.updateCtaLabel?.value), 1)
              ], 8, _hoisted_5)
            ])
          ]))
        : (isSubmitted.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _cache[2] || (_cache[2] = _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                width: "88",
                height: "89",
                viewBox: "0 0 88 89",
                fill: "none"
              }, [
                _createElementVNode("path", {
                  "fill-rule": "evenodd",
                  "clip-rule": "evenodd",
                  d: "M44 0.494141C19.74 0.494141 0 20.2341 0 44.4941C0 68.7541 19.74 88.4941 44 88.4941C68.26 88.4941 88 68.7541 88 44.4941C88 20.2341 68.26 0.494141 44 0.494141ZM44 83.4941C22.5 83.4941 5 65.9941 5 44.4941C5 22.9941 22.5 5.49414 44 5.49414C65.5 5.49414 83 22.9941 83 44.4941C83 65.9941 65.5 83.4941 44 83.4941Z",
                  fill: "#128750"
                }),
                _createElementVNode("path", {
                  "fill-rule": "evenodd",
                  "clip-rule": "evenodd",
                  d: "M63.72 25.6441V25.4941C62.67 25.4941 61.62 26.0941 61.01 26.8441L37.71 53.8941L26.74 42.6241C25.24 41.1241 22.68 40.9741 21.18 42.6241C19.68 44.1241 19.53 46.6841 21.18 48.1841L35.16 62.6141C36.66 64.2641 39.22 64.2641 40.72 62.6141C40.72 62.6141 40.87 62.4641 41.02 62.3141L67.02 32.1041C68.52 30.4541 68.22 27.8941 66.57 26.5441C65.82 25.7941 64.77 25.4941 63.71 25.6441H63.72Z",
                  fill: "#128750"
                })
              ], -1)),
              _createVNode(_unref(RichText), {
                field: { value: autoRenewSuccessMessage.value }
              }, null, 8, ["field"]),
              _createElementVNode("button", {
                class: "btn btn-primary btn-md mt-12",
                onClick: closeModal
              }, "Done")
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _cache[3] || (_cache[3] = _createElementVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                width: "88",
                height: "88",
                viewBox: "0 0 88 88",
                fill: "none"
              }, [
                _createElementVNode("path", {
                  d: "M44 -0.00488281C19.7343 -0.00488281 0 19.5051 0 43.4951V44.5051C0 68.4851 19.7343 88.0051 44 88.0051C68.2657 88.0051 88 68.4951 88 44.5051V43.4951C87.9899 19.5051 68.2556 -0.00488281 44 -0.00488281ZM82.9324 44.4951C82.9324 65.7251 65.4639 82.9951 43.9899 82.9951C22.5159 82.9951 5.05747 65.7251 5.05747 44.4951V43.4851C5.05747 22.2651 22.526 4.99512 44 4.99512C65.474 4.99512 82.9425 22.2651 82.9425 43.4951V44.5051L82.9324 44.4951Z",
                  fill: "#D82900"
                }),
                _createElementVNode("path", {
                  d: "M60.8009 27.0251C59.6175 25.8551 57.6956 25.8551 56.5122 27.0251L43.6359 39.7551L31.4777 27.7351C30.2943 26.5651 28.3724 26.5651 27.189 27.7351C26.0055 28.9051 26.0055 30.8051 27.189 31.9751L39.3471 43.9951L27.189 56.0151C26.0055 57.1851 26.0055 59.0851 27.189 60.2551C28.3724 61.4251 30.2943 61.4251 31.4777 60.2551L43.6359 48.2351L56.5122 60.9651C57.6956 62.1351 59.6175 62.1351 60.8009 60.9651C61.9844 59.7951 61.9844 57.8951 60.8009 56.7251L47.9246 43.9951L60.8009 31.2651C61.9844 30.0951 61.9844 28.1951 60.8009 27.0251Z",
                  fill: "#D82900"
                })
              ], -1)),
              _createVNode(_unref(RichText), {
                field: props.autoRenewSettingsModal.failureMessage,
                class: "text-center"
              }, null, 8, ["field"]),
              _createElementVNode("button", {
                class: "btn btn-primary btn-md mt-6",
                onClick: closeModal
              }, "Done")
            ]))
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})